import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

const client = axios.create({ baseURL: API, timeout: 30000 });

export const api = {
  status: () => client.get("/scanner/status").then((r) => r.data),
  pairs: (minVolume = 2_000_000) =>
    client.get("/pairs", { params: { min_volume: minVolume } }).then((r) => r.data),
  liveSignals: (minScore = 0, direction = "all", mode = "all") =>
    client
      .get("/signals/live", { params: { min_score: minScore, direction, mode } })
      .then((r) => r.data),
  signalsHistory: (params = {}) =>
    client.get("/signals/history", { params }).then((r) => r.data),
  signalsStats: (params = {}) =>
    client.get("/signals/stats", { params }).then((r) => r.data),
  analysis: (symbol, interval = "5m", direction = "long", mode = "scalp") =>
    client
      .get(`/pairs/${symbol}/analysis`, { params: { interval, direction, mode } })
      .then((r) => r.data),
  getWatchlist: () => client.get("/watchlist").then((r) => r.data),
  setWatchlist: (symbols) =>
    client.put("/watchlist", { symbols }).then((r) => r.data),
  fvg: (symbol) => client.get(`/pairs/${symbol}/fvg`).then((r) => r.data),
  klines: (symbol, interval = "5m", limit = 200) =>
    client
      .get(`/pairs/${symbol}/klines`, { params: { interval, limit } })
      .then((r) => r.data),
  backtest: (payload) => client.post("/backtest", payload).then((r) => r.data),
  refreshScanner: () => client.post("/scanner/refresh").then((r) => r.data),
  engineLearning: (days = 30) =>
    client.get("/engine/learning", { params: { days } }).then((r) => r.data),
  triggerHistory: (limit = 50) =>
    client.get("/triggers/history", { params: { limit } }).then((r) => r.data),
  engineRelearn: () => client.post("/engine/relearn").then((r) => r.data),
  engineReset: () => client.post("/engine/reset").then((r) => r.data),
  engineResetStats: () => client.post("/engine/reset-stats").then((r) => r.data),
  winnerProfileStart: (n_pairs = 20, days = 60) =>
    client
      .post("/engine/winner-profile", null, { params: { n_pairs, days } })
      .then((r) => r.data),
  winnerProfileResult: () =>
    client.get("/engine/winner-profile").then((r) => r.data),
  applySuggestedWeights: () =>
    client.post("/engine/apply-suggested-weights").then((r) => r.data),
  modeOverrides: () => client.get("/engine/mode-overrides").then((r) => r.data),
  resetModeOverrides: () =>
    client.post("/engine/mode-overrides/reset").then((r) => r.data),
  // Self-Evolving indicators (auto-discovered conjunction rules)
  learnedIndicators: (includeDisabled = false) =>
    client
      .get("/engine/learned-indicators", { params: { include_disabled: includeDisabled } })
      .then((r) => r.data),
  discoverLearnedIndicators: () =>
    client.post("/engine/learned-indicators/discover").then((r) => r.data),
  toggleLearnedIndicator: (ruleId, enabled) =>
    client
      .put(`/engine/learned-indicators/${ruleId}`, { enabled })
      .then((r) => r.data),
  resetLearnedIndicators: () =>
    client.delete("/engine/learned-indicators").then((r) => r.data),
  backtestStress: (body) =>
    client.post("/backtest/stress", body).then((r) => r.data),
  killSwitchReset: () => client.post("/scanner/kill-switch/reset").then((r) => r.data),
  // News blackout
  newsUpcoming: (hours = 168) => client.get("/news/upcoming", { params: { hours } }).then((r) => r.data),
  addNewsEvent: (payload) => client.post("/news/events", payload).then((r) => r.data),
  deleteNewsEvent: (id) => client.delete(`/news/events/${id}`).then((r) => r.data),
  // Telegram
  telegramStatus: () => client.get("/telegram/status").then((r) => r.data),
  telegramDiscover: () => client.post("/telegram/discover").then((r) => r.data),
  telegramRegister: (chat_id) => client.post("/telegram/register", { chat_id }).then((r) => r.data),
  telegramConfig: (payload) => client.put("/telegram/config", payload).then((r) => r.data),
  // Walk-forward
  walkForward: (direction = "long", n_folds = 4) =>
    client.get("/engine/walk-forward", { params: { direction, n_folds } }).then((r) => r.data),
  // Equity curve
  equityCurve: (days = 30, leveraged = true) =>
    client.get("/signals/equity-curve", { params: { days, leveraged } }).then((r) => r.data),
  // Filter presets
  signalPresets: () => client.get("/signals/presets").then((r) => r.data),
  setSignalPreset: (preset) => client.put("/signals/presets", { preset }).then((r) => r.data),
  // Universe coverage
  pairsCoverage: (mode = "swing", limit = 150) =>
    client.get("/pairs/coverage", { params: { mode, limit } }).then((r) => r.data),
  pairsCoverageMulti: (limit = 150) =>
    client.get("/pairs/coverage-multi", { params: { limit } }).then((r) => r.data),
  // Cryptometer.io alpha
  cryptometerSnapshot: () =>
    client.get("/cryptometer/snapshot").then((r) => r.data),
  cryptometerSymbol: (symbol) =>
    client.get(`/cryptometer/snapshot/${symbol}`).then((r) => r.data),
  cryptometerWeights: () =>
    client.get("/cryptometer/weights").then((r) => r.data),
  cryptometerToggleSource: (sourceId, enabled) =>
    client
      .put(`/cryptometer/weights/${sourceId}/enabled`, { enabled })
      .then((r) => r.data),
  cryptometerResetWeights: () =>
    client.post("/cryptometer/weights/reset").then((r) => r.data),
  cryptometerRefresh: () =>
    client.post("/cryptometer/refresh").then((r) => r.data),
};

export function fmtNum(n, digits = 2) {
  if (n === null || n === undefined || Number.isNaN(n)) return "-";
  const abs = Math.abs(n);
  if (abs >= 1e9) return (n / 1e9).toFixed(2) + "B";
  if (abs >= 1e6) return (n / 1e6).toFixed(2) + "M";
  if (abs >= 1e3) return (n / 1e3).toFixed(2) + "K";
  if (abs >= 1) return n.toFixed(digits);
  if (abs === 0) return "0";
  // dynamic precision for small crypto prices
  const magnitude = Math.max(2, Math.ceil(-Math.log10(abs)) + 3);
  return n.toFixed(magnitude);
}

export function timeAgo(iso) {
  if (!iso) return "-";
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return `${s}s ago`;
  const m = Math.floor(s / 60);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.floor(h / 24)}d ago`;
}
